import { useState, type JSX } from 'react';
import type { Company } from '@/data/schemas';
import { DataList, Panel } from '@/components/blocks/data-display';
import { generateDisplaySections } from '@/utils/generateDisplaySections';
import { companyFormFields, companySections } from './companyFormSchema';

/**
 * Format a string field value, returning "—" for empty/null values
 */
function formatField(value: string | null | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : '—';
}

export interface CompanyAnagraficaProps {
  /** Company data to display */
  company: Company | null | undefined;
}

/**
 * Encapsulated anagrafica component for company details.
 * Sections auto-generated from schema, with manual logo and notes panels.
 */
export function CompanyAnagrafica({ company }: CompanyAnagraficaProps): JSX.Element {
  // Auto-generate sections from schema
  const sections = generateDisplaySections(
    company as Record<string, unknown> | undefined,
    companyFormFields,
    companySections,
    {
      text: (v) => formatField(v as string | null | undefined),
      date: () => '—', // Company doesn't have date fields
      boolean: () => '—', // Company doesn't have boolean fields
      number: (v) => (v != null ? String(v) : '—'),
      'month-year': () => '—', // Company doesn't have month-year fields
    }
  );

  const notes = company?.notes?.trim();

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <Panel title="Logo aziendale">
        <CompanyLogoThumbnail logoUrl={company?.logoUrl} size="sm" />
      </Panel>
      {sections.map((section) => (
        <Panel key={section.key} title={section.title}>
          <DataList items={section.items} />
        </Panel>
      ))}
      <Panel title="Note">
        <DataList
          items={[
            {
              label: 'Note',
              value: notes?.length ? notes : '—',
              layout: 'stacked',
              valueClassName: 'text-sm font-normal text-text-primary whitespace-pre-wrap',
            },
          ]}
        />
      </Panel>
    </div>
  );
}

function CompanyLogoThumbnail({
  logoUrl,
  size = 'md',
}: {
  logoUrl?: string | null;
  size?: 'sm' | 'md';
}) {
  const [hasError, setHasError] = useState(false);
  const normalizedUrl = logoUrl?.trim() || undefined;
  const showFallback = !normalizedUrl || hasError;
  const sizeClass = size === 'sm' ? 'h-[72px] w-[120px]' : 'h-[96px] w-[160px]';

  return (
    <div
      className={
        showFallback
          ? `flex ${sizeClass} items-center justify-center rounded-md border border-[var(--border-default)] bg-neutral-100 p-2`
          : `flex ${sizeClass} items-center justify-center rounded-md border border-[var(--border-default)] bg-surface p-2`
      }
      role={showFallback ? 'img' : undefined}
      aria-label={showFallback ? 'Logo non disponibile' : undefined}
    >
      {showFallback ? (
        <span className="text-xs font-semibold leading-tight text-center text-text-secondary">
          Logo non disponibile
        </span>
      ) : (
        <img
          src={normalizedUrl}
          alt="Logo aziendale"
          className="max-h-full max-w-full object-contain"
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
}
